/**
 * Role Constants and Utilities
 * Provides centralized role management for the application
 */

// Role Types
export const ROLES = {
  SUPER_ADMIN: 'super_admin',
  ADMIN: 'admin', 
  SELLER: 'seller',
  SERVICE: 'service',
  CUSTOMER: 'customer'
};

// Role Hierarchies - Higher number = more permissions
export const ROLE_HIERARCHY = {
  [ROLES.SUPER_ADMIN]: 100,
  [ROLES.ADMIN]: 50,
  [ROLES.SELLER]: 25,
  [ROLES.SERVICE]: 20,
  [ROLES.CUSTOMER]: 10
};

// Role-based dashboard routes
export const ROLE_DASHBOARDS = {
  [ROLES.SUPER_ADMIN]: '/dashboard',
  [ROLES.ADMIN]: '/dashboard', 
  [ROLES.SELLER]: '/seller/dashboard',
  [ROLES.SERVICE]: '/service-provider/dashboard',
  [ROLES.CUSTOMER]: '/customer/dashboard'
};

// Default permissions for each role
export const ROLE_PERMISSIONS = {
  [ROLES.SUPER_ADMIN]: ['*'], // Wildcard - all permissions
  [ROLES.ADMIN]: [
    'view_product', 'add_product', 'edit_product', 'delete_product',
    'view_order', 'edit_order', 'delete_order',
    'view_customer', 'view_admin', 'add_admin', 'edit_admin',
    'view_role', 'add_role', 'edit_role', 'delete_role',
    'view_promotion', 'add_promotion', 'edit_promotion', 'delete_promotion',
    'view_coupon', 'add_coupon', 'delete_coupon',
    'view_banner', 'view_media', 'view_category'
  ],
  [ROLES.SELLER]: [
    'view_product', 'add_product', 'edit_product', 'delete_product',
    'view_media', 'add_media', 'edit_media', 'delete_media',
    'view_notification', 'view_admin_alert',
    'view_sku', 'add_sku', 'edit_sku', 'delete_sku'
  ],
  [ROLES.SERVICE]: [
    'view_service', 'edit_service',
    'view_request_services', 'edit_request_services',
    'view_notification'
  ],
  [ROLES.CUSTOMER]: [
    'view_profile', 'edit_profile'
  ]
};

/**
 * Role Utility Functions
 */
export class RoleUtils {
  
  /**
   * Determine user role from permissions array
   * @param {string[]} permissions - Array of permission strings
   * @returns {string} Role type
   */
  static getRoleFromPermissions(permissions = []) {
    if (!Array.isArray(permissions) || permissions.length === 0) {
      return ROLES.CUSTOMER;
    }
    
    // Check for super admin (wildcard permission)
    if (permissions.includes('*')) {
      return ROLES.SUPER_ADMIN;
    }
    
    // Check for admin-level permissions
    const adminPermissions = ['view_role', 'add_role', 'view_admin'];
    if (adminPermissions.some(perm => permissions.includes(perm))) {
      return ROLES.ADMIN;
    }
    
    // Check for seller-level permissions
    const sellerPermissions = ['view_product', 'add_product'];
    if (sellerPermissions.some(perm => permissions.includes(perm))) {
      return ROLES.SELLER;
    }
    
    // Check for service-level permissions
    const servicePermissions = ['view_service', 'view_request_services'];
    if (servicePermissions.some(perm => permissions.includes(perm))) {
      return ROLES.SERVICE;
    }
    
    return ROLES.CUSTOMER;
  }

  /**
   * Check if user has a specific role
   * @param {string} userRole - User's role
   * @param {string} requiredRole - Required role
   * @returns {boolean}
   */
  static hasRole(userRole, requiredRole) {
    return userRole === requiredRole;
  }

  /**
   * Check if user role has equal or higher hierarchy than required role
   * @param {string} userRole - User's role
   * @param {string} requiredRole - Required role
   * @returns {boolean}
   */
  static hasRoleOrHigher(userRole, requiredRole) {
    const userLevel = ROLE_HIERARCHY[userRole] || 0;
    const requiredLevel = ROLE_HIERARCHY[requiredRole] || 0;
    return userLevel >= requiredLevel;
  }

  /**
   * Get default dashboard route for role
   * @param {string} role - User role
   * @returns {string} Dashboard route
   */
  static getDashboardRoute(role) {
    return ROLE_DASHBOARDS[role] || '/login';
  }

  /**
   * Check if role is admin-level (admin or super admin)
   * @param {string} role - User role
   * @returns {boolean}
   */
  static isAdminLevel(role) {
    return [ROLES.SUPER_ADMIN, ROLES.ADMIN].includes(role);
  }

  /**
   * Check if role is seller-level
   * @param {string} role - User role 
   * @returns {boolean}
   */
  static isSellerLevel(role) {
    return role === ROLES.SELLER;
  }

  /**
   * Check if role is service-level
   * @param {string} role - User role 
   * @returns {boolean}
   */
  static isServiceLevel(role) {
    return role === ROLES.SERVICE;
  }

  /**
   * Get role display name
   * @param {string} role - Role constant
   * @returns {string} Human-readable role name
   */
  static getRoleDisplayName(role) {
    const displayNames = {
      [ROLES.SUPER_ADMIN]: 'Super Administrator',
      [ROLES.ADMIN]: 'Administrator',
      [ROLES.SELLER]: 'Seller',
      [ROLES.SERVICE]: 'Service Provider',
      [ROLES.CUSTOMER]: 'Customer'
    };
    return displayNames[role] || 'Unknown Role';
  }
}

export default RoleUtils;
